import { camelCaseObject } from '@edx/frontend-platform';
import { logError } from '@edx/frontend-platform/logging';

import {
  PostsStatusFilter, ThreadType,
} from '../../../data/constants';
import { selectEnableDiscussionBan } from '../../data/selectors';
import { setContentCreationRateLimited } from '../../data/slices';
import { getHttpErrorStatus } from '../../utils';
import {
  deleteThread, getThread, getThreads, postThread, sendEmailForAccountActivation, updateThread,
} from './api';
import {
  deleteThreadDenied,
  deleteThreadFailed,
  deleteThreadRequest,
  deleteThreadSuccess,
  fetchThreadByDirectLinkSuccess,
  fetchThreadDenied,
  fetchThreadFailed,
  fetchThreadRequest,
  fetchThreadsDenied,
  fetchThreadsFailed,
  fetchThreadsRequest,
  fetchThreadsSuccess,
  fetchThreadSuccess,
  hidePostEditor,
  postThreadDenied,
  postThreadFailed,
  postThreadRequest,
  postThreadSuccess,
  sendAccountActivationEmailDenied,
  sendAccountActivationEmailFailed,
  sendAccountActivationEmailRequest,
  sendAccountActivationEmailSuccess,
  updateThreadAsRead,
  updateThreadDenied,
  updateThreadFailed,
  updateThreadRequest,
  updateThreadSuccess,
} from './slices';

/**
 * Filters to apply to a thread/posts query.
 * @typedef {Object} ThreadFilter
 * @property {PostsStatusFilter} status
 * @property {ThreadType} postType
 */

/**
 * Normalises raw data returned by threads API by mapping threads to id and
 * mapping topic ids to threads in them.
 * @param data
 * @param {[string]?} topicIds
 * @returns {{pagination, threadsById: {}, threadsInTopic: {}, avatars: {}}}
 */
export function normaliseThreads(data, topicIds = null) {
  const normalized = {};
  let threads;
  if ('results' in data) {
    threads = data.results;
    normalized.pagination = data.pagination;
  } else {
    threads = [data];
  }
  const threadsInTopic = {};
  const threadsById = {};
  let avatars = {};
  const ids = [];
  const seenInTopics = {};
  if (topicIds) {
    topicIds.forEach(topicId => {
      threadsInTopic[topicId] = [];
      seenInTopics[topicId] = new Set();
    });
  }
  threads.forEach(
    thread => {
      const { topicId, id } = thread;
      ids.push(id);
      if (!threadsInTopic[topicId]) {
        threadsInTopic[topicId] = [];
        seenInTopics[topicId] = new Set();
      }
      if (!seenInTopics[topicId].has(id)) {
        seenInTopics[topicId].add(id);
        threadsInTopic[topicId].push(id);
      }
      // Normalize editableFields to always be an array
      threadsById[id] = {
        ...thread,
        editableFields: thread.editableFields || [],
      };
      avatars = { ...avatars, ...thread.users };
    },
  );
  return {
    ids, threadsById, threadsInTopic, avatars, ...normalized,
  };
}

const pendingThreadsFetches = new Set();

/**
 * Fetches the threads for the course specified va the threadIds.
 * @param {string} courseId The course ID for the course to fetch data for.
 * @param {?string} author The author whose posts need to be viewed.
 * @param {[string]} topicIds List of topics to limit threads to
 * @param {ThreadOrdering} orderBy The results will be sorted on this basis.
 * @param {ThreadFilter} filters The set of filters to apply to the thread.
 * @param {number} page Page to fetch
 * @param {boolean} countFlagged
 * @returns {(function(*): Promise<void>)|*}
 */
export function fetchThreads(courseId, {
  topicIds,
  orderBy,
  author = null,
  filters = {},
  page = 1,
  isFilterChanged,
  countFlagged,
  includeMuted,
} = {}) {
  const options = {
    orderBy,
    topicIds,
    page,
    author,
    countFlagged,
    includeMuted,
  };
  if (filters.status === PostsStatusFilter.FOLLOWING) {
    options.following = true;
  }
  if (filters.status === PostsStatusFilter.UNREAD) {
    options.view = 'unread';
  }
  if (filters.status === PostsStatusFilter.UNANSWERED) {
    options.view = 'unanswered';
  }
  if (filters.status === PostsStatusFilter.UNRESPONDED) {
    options.view = 'unresponded';
  }
  if (filters.status === PostsStatusFilter.REPORTED) {
    options.flagged = true;
  }
  if (filters.postType !== ThreadType.ALL) {
    options.threadType = filters.postType;
  }
  if (filters.search) {
    options.textSearch = filters.search;
  }
  if (filters.cohort) {
    options.cohort = filters.cohort;
  }
  if (filters.status === PostsStatusFilter.ACTIVE) {
    options.isDeleted = false;
  }
  if (filters.status === PostsStatusFilter.DELETED) {
    options.isDeleted = true;
  }
  return async (dispatch, getState) => {
    const requestKey = `${courseId}|${page}|${options.orderBy}|${options.author}|${options.following}|${options.view}|${options.flagged}|${options.threadType}|${options.textSearch}|${options.cohort}|${options.isDeleted}|${options.includeMuted}|${options.countFlagged}|${(options.topicIds || []).join(',')}`;
    if (pendingThreadsFetches.has(requestKey)) {
      return;
    }
    pendingThreadsFetches.add(requestKey);
    try {
      dispatch(fetchThreadsRequest({ courseId }));
      const enableDiscussionBan = selectEnableDiscussionBan(getState());
      const data = await getThreads(courseId, { ...options, enableDiscussionBan });
      const normalisedData = normaliseThreads(camelCaseObject(data), topicIds);
      dispatch(fetchThreadsSuccess({
        ...normalisedData, page, author, textSearchRewrite: data.text_search_rewrite, isFilterChanged,
      }));
    } catch (error) {
      if (getHttpErrorStatus(error) === 403) {
        dispatch(fetchThreadsDenied());
      } else {
        dispatch(fetchThreadsFailed());
      }
      logError(error);
    } finally {
      pendingThreadsFetches.delete(requestKey);
    }
  };
}

/**
 * Fetches a single thread by ID.
 * @param {string} threadId The ID of the thread to fetch.
 * @param {?string} courseId The course ID. If not provided, it will be derived from Redux state.
 * @param {boolean} isDirectLinkPost Whether this is a direct link to a post.
 * @param {boolean} includeMuted Whether to include muted content.
 * @returns {function(*): Promise<void>}
 */
export function fetchThread(threadId, courseId, isDirectLinkPost = false, includeMuted = false) {
  return async (dispatch, getState) => {
    try {
      dispatch(fetchThreadRequest({ threadId }));
      // Maintain backward compatibility: derive courseId from state if not provided
      const actualCourseId = courseId || getState().config.courseId;
      const enableDiscussionBan = selectEnableDiscussionBan(getState());
      const data = await getThread(threadId, actualCourseId, enableDiscussionBan, includeMuted);
      if (isDirectLinkPost) {
        dispatch(fetchThreadByDirectLinkSuccess({ ...normaliseThreads(camelCaseObject(data)), page: 1 }));
      } else {
        dispatch(fetchThreadSuccess(normaliseThreads(camelCaseObject(data))));
      }
    } catch (error) {
      if (getHttpErrorStatus(error) === 403 || getHttpErrorStatus(error) === 404) {
        dispatch(fetchThreadDenied());
      } else {
        dispatch(fetchThreadFailed());
      }
      logError(error);
    }
  };
}

export function markThreadAsRead(threadId) {
  return async (dispatch) => {
    try {
      dispatch(updateThreadAsRead({ threadId }));
      dispatch(updateThreadRequest({ threadId, read: true }));
      const data = await updateThread(threadId, { read: true });
      dispatch(updateThreadSuccess(camelCaseObject(data)));
    } catch (error) {
      if (getHttpErrorStatus(error) === 403) {
        dispatch(updateThreadDenied());
      } else {
        dispatch(updateThreadFailed());
      }
      logError(error);
    }
  };
}

export function createNewThread({
  courseId,
  topicId,
  type,
  title,
  content,
  following,
  anonymous,
  anonymousToPeers,
  cohort,
  enableInContextSidebar,
  notifyAllLearners,
  recaptchaToken,
}) {
  return async (dispatch) => {
    try {
      dispatch(postThreadRequest({
        courseId,
        topicId,
        type,
        title,
        content,
        following,
        anonymous,
        anonymousToPeers,
        cohort,
        notifyAllLearners,
        recaptchaToken,
      }));
      const data = await postThread(courseId, topicId, type, title, content, {
        cohort,
        following,
        anonymous,
        anonymousToPeers,
        notifyAllLearners,
        recaptchaToken,
      }, enableInContextSidebar);
      dispatch(postThreadSuccess(camelCaseObject(data)));
      dispatch(hidePostEditor());
    } catch (error) {
      if (getHttpErrorStatus(error) === 403) {
        dispatch(postThreadDenied());
      } else if (getHttpErrorStatus(error) === 429) {
        dispatch(setContentCreationRateLimited());
      } else {
        dispatch(postThreadFailed());
      }
      logError(error);
    }
  };
}

export function updateExistingThread(threadId, {
  flagged, voted, read, topicId, type, title, content, following, closed, pinned, closeReasonCode, editReasonCode,
}) {
  return async (dispatch) => {
    try {
      dispatch(updateThreadRequest({
        threadId,
        flagged,
        voted,
        read,
        topicId,
        type,
        title,
        content,
        following,
        closed,
        pinned,
        editReasonCode,
        closeReasonCode,
      }));
      const data = await updateThread(threadId, {
        flagged,
        voted,
        read,
        topicId,
        type,
        title,
        content,
        following,
        closed,
        pinned,
        editReasonCode,
        closeReasonCode,
      });
      dispatch(updateThreadSuccess(camelCaseObject(data)));
    } catch (error) {
      if (getHttpErrorStatus(error) === 403) {
        dispatch(updateThreadDenied());
      } else {
        dispatch(updateThreadFailed());
      }
      logError(error);
    }
  };
}

export function removeThread(threadId) {
  return async (dispatch) => {
    try {
      dispatch(deleteThreadRequest({ threadId }));
      await deleteThread(threadId);
      dispatch(deleteThreadSuccess({ threadId }));
    } catch (error) {
      if (getHttpErrorStatus(error) === 403) {
        dispatch(deleteThreadDenied());
      } else {
        dispatch(deleteThreadFailed());
      }
      logError(error);
    }
  };
}

export function performRestoreThread(threadId, courseId) {
  return async (dispatch) => {
    try {
      const { restoreThread } = await import('./api');
      await restoreThread(threadId, courseId);
      // Fetch the updated thread to get the current state
      await dispatch(fetchThread(threadId, courseId, false));
      return { success: true };
    } catch (error) {
      logError(error);
      return { success: false, error: error.message };
    }
  };
}

export function sendAccountActivationEmail() {
  return async (dispatch) => {
    try {
      dispatch(sendAccountActivationEmailRequest());
      const data = await sendEmailForAccountActivation();
      dispatch(sendAccountActivationEmailSuccess(camelCaseObject(data)));
    } catch (error) {
      if (getHttpErrorStatus(error) === 403) {
        dispatch(sendAccountActivationEmailDenied());
      } else {
        dispatch(sendAccountActivationEmailFailed());
      }
      logError(error);
    }
  };
}
